import { useAuth } from "react-oidc-context";
import React, { FC, useEffect, useState , useRef} from "react";
import AuthContext from "../../contexts/AuthContext";
import { ToastContainer, toast } from "react-toastify";
import App from "../../App";
import { Dimmer, Loader } from "semantic-ui-react";
import { setGlobalAuth } from "../../services/axiosService";

interface OpenIdProps {
    openIdSettings: any;
    setOpenIdSettings: any;
}


const OpenId: FC<OpenIdProps> = ({ openIdSettings, setOpenIdSettings }) => {
    const auth = useAuth();
    const [sessionExpired, setSessionExpired] = useState(false);
    const [showSaveWarning, setShowSaveWarning] = useState(false);

    // Track warnings to prevent duplicates
    const warningShownRef = useRef({ fiveMinute: false, final: false });
    const redirectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        // Set global auth reference for axios interceptors
        setGlobalAuth(auth);

        // Clean up expired localStorage items periodically
        cleanupExpiredStorageItems();

        // Set up token expiry check with two-stage warnings
        if (auth.isAuthenticated && auth.user?.access_token) {
            const checkTokenExpiry = () => {
                try {
                    const token = auth.user?.access_token;
                    if (!token) return;

                    const payload = JSON.parse(atob(token.split('.')[1]));
                    const currentTime = Math.floor(Date.now() / 1000);
                    const timeUntilExpiry = payload.exp - currentTime;

                    // 5-minute warning: Prompt user to save work
                    if (timeUntilExpiry <= 300 && timeUntilExpiry > 120 && !warningShownRef.current.fiveMinute) {
                        console.log('Token expiring in', timeUntilExpiry, 'seconds. Showing save warning...');

                        warningShownRef.current.fiveMinute = true;
                        setShowSaveWarning(true);

                        toast.warning('⚠️ Your session will expire in 5 minutes. Please save any unsaved work!', {
                            autoClose: 10000, // Show for 10 seconds
                            hideProgressBar: false,
                            closeOnClick: true,
                            pauseOnHover: true,
                        });
                    }
                    // 2-minute warning: Final warning before redirect
                    else if (timeUntilExpiry <= 120 && timeUntilExpiry > 0 && !warningShownRef.current.final) {
                        console.log('Token expiring in', timeUntilExpiry, 'seconds. Preparing to redirect...');

                        warningShownRef.current.final = true;
                        setShowSaveWarning(false); // Hide save warning modal

                        // Clean up storage before redirect
                        cleanupExpiredStorageItems();

                        // Show final warning with countdown
                        const countdownToast = toast.warning('Session expiring soon! Redirecting in 10 seconds to refresh...', {
                            autoClose: 10000,
                            hideProgressBar: false,
                            closeOnClick: false,
                            pauseOnHover: false,
                            onClose: () => {
                                // Redirect to renew session (no iframe)
                                auth.signinRedirect();
                            }
                        });

                        // Set backup redirect timeout in case toast fails
                        redirectTimeoutRef.current = setTimeout(() => {
                            auth.signinRedirect();
                        }, 10000);
                    }
                    // Token already expired
                    else if (timeUntilExpiry <= 0) {
                        setSessionExpired(true);
                    }
                } catch (error) {
                    console.error('Error checking token expiry:', error);
                    setSessionExpired(true);
                }
            };

            // Check token expiry every minute for better precision
            const interval = setInterval(checkTokenExpiry, 60 * 1000);

            // Initial check
            checkTokenExpiry();

            return () => {
                clearInterval(interval);
                if (redirectTimeoutRef.current) {
                    clearTimeout(redirectTimeoutRef.current);
                }
            };
        }
    }, [auth]);

    // Reset warning flags when user gets new token
    useEffect(() => {
        if (auth.isAuthenticated && auth.user?.access_token) {
            warningShownRef.current = { fiveMinute: false, final: false };
            setShowSaveWarning(false);
            if (redirectTimeoutRef.current) {
                clearTimeout(redirectTimeoutRef.current);
                redirectTimeoutRef.current = null;
            }
        }
    }, [auth.user?.access_token]);

    // Handle errors
    useEffect(() => {
        if (auth.error) {
            console.error('Auth error:', auth.error);

            // Handle session expiration errors
            if (
                auth.error.message === "login_required" ||
                auth.error.message === "interaction_required" ||
                auth.error.message?.includes('token_expired')
            ) {
                setSessionExpired(true);
                return;
            }

            // Handle storage quota errors
            if (
                auth.error.message?.includes('QuotaExceededError') ||
                auth.error.message?.includes('setItem') ||
                auth.error.name === 'QuotaExceededError'
            ) {
                handleStorageQuotaExceeded();
                return;
            }

            // Show other auth errors
            if (!auth.error.message?.includes('silent')) {
                toast.error(`Authentication error: ${auth.error.message}`);
            }
        }
    }, [auth.error]);

    const handleDismissSaveWarning = () => {
        setShowSaveWarning(false);
    };

    const handleExtendSession = () => {
        setShowSaveWarning(false);
        toast.info('Redirecting to extend your session...', {
            autoClose: 3000,
            onClose: () => {
                auth.signinRedirect();
            }
        });
    };

    const cleanupExpiredStorageItems = () => {
        try {
            const keysToCheck: any[] = [];
            const currentTime = Date.now() / 1000;

            // Check localStorage and sessionStorage
            [localStorage, sessionStorage].forEach(storage => {
                for (let i = 0; i < storage.length; i++) {
                    const key = storage.key(i);
                    if (key) keysToCheck.push({ key, storage });
                }
            });

            // Clean up OIDC-related items and expired tokens
            keysToCheck.forEach(({ key, storage }) => {
                if (key.includes('oidc') || key.includes('token') || key.includes('auth')) {
                    try {
                        const item = storage.getItem(key);
                        if (item) {
                            try {
                                const parsed = JSON.parse(item);
                                if (parsed.expires_at && parsed.expires_at < currentTime) {
                                    storage.removeItem(key);
                                    console.log(`Removed expired item: ${key}`);
                                }
                                else if (key.startsWith('oidc.user:') && parsed.access_token) {
                                    const tokenPayload = JSON.parse(atob(parsed.access_token.split('.')[1]));
                                    if (tokenPayload.exp && tokenPayload.exp < currentTime) {
                                        storage.removeItem(key);
                                        console.log(`Removed expired token user entry: ${key}`);
                                    }
                                }
                            } catch (parseError) {
                                if (item && isExpiredJWT(item)) {
                                    storage.removeItem(key);
                                    console.log(`Removed expired JWT: ${key}`);
                                }
                            }
                        }
                    } catch (error) {
                        console.error(`Error processing storage key ${key}:`, error);
                    }
                }
            });

            // Remove old entries if storage is getting full
            if (isStorageNearCapacity()) {
                cleanupOldEntries();
            }

            // Clean up duplicate OIDC user entries
            cleanupDuplicateOidcEntries();

        } catch (error) {
            console.error('Error cleaning up storage:', error);
        }
    };

    const cleanupDuplicateOidcEntries = () => {
        try {
            const oidcUserKeys = [];

            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith('oidc.user:')) {
                    oidcUserKeys.push(key);
                }
            }

            if (oidcUserKeys.length > 1) {
                const entries = oidcUserKeys.map(key => {
                    try {
                        const item = localStorage.getItem(key);
                        const parsed = item ? JSON.parse(item) : null;
                        return {
                            key,
                            timestamp: parsed?.profile?.iat || parsed?.expires_at || 0
                        };
                    } catch (error) {
                        return { key, timestamp: 0 };
                    }
                });

                entries.sort((a, b) => b.timestamp - a.timestamp);

                for (let i = 1; i < entries.length; i++) {
                    localStorage.removeItem(entries[i].key);
                    console.log(`Removed duplicate OIDC entry: ${entries[i].key}`);
                }
            }
        } catch (error) {
            console.error('Error cleaning up duplicate OIDC entries:', error);
        }
    };

    const isExpiredJWT = (token: string): boolean => {
        try {
            if (!token || typeof token !== 'string' || token.split('.').length !== 3) {
                return false;
            }

            const payload = JSON.parse(atob(token.split('.')[1]));
            const currentTime = Math.floor(Date.now() / 1000);
            return payload.exp && payload.exp < currentTime;
        } catch (error) {
            return false;
        }
    };

    const isStorageNearCapacity = (): boolean => {
        try {
            const testKey = 'storage_test_' + Date.now();
            const testData = 'test';

            localStorage.setItem(testKey, testData);
            localStorage.removeItem(testKey);
            return false;
        } catch (error) {
            return true;
        }
    };

    const cleanupOldEntries = () => {
        try {
            const entries: Array<{ key: string, timestamp: number, storage: Storage }> = [];

            [localStorage, sessionStorage].forEach(storage => {
                for (let i = 0; i < storage.length; i++) {
                    const key = storage.key(i);
                    if (key && !key.startsWith('oidc.user:') && !key.startsWith('favourites')) {
                        try {
                            const item = storage.getItem(key);
                            if (item) {
                                const parsed = JSON.parse(item);
                                const timestamp = parsed.timestamp || parsed.created_at || 0;
                                entries.push({ key, timestamp, storage });
                            }
                        } catch (error) {
                            entries.push({ key, timestamp: 0, storage });
                        }
                    }
                }
            });

            entries.sort((a, b) => a.timestamp - b.timestamp);
            const toRemove = Math.floor(entries.length * 0.2);

            for (let i = 0; i < toRemove; i++) {
                try {
                    entries[i].storage.removeItem(entries[i].key);
                    console.log(`Removed old entry: ${entries[i].key}`);
                } catch (error) {
                    console.error(`Error removing ${entries[i].key}:`, error);
                }
            }
        } catch (error) {
            console.error('Error cleaning up old entries:', error);
        }
    };

    const handleStorageQuotaExceeded = () => {
        console.warn('Storage quota exceeded, attempting cleanup...');
        toast.warning('Storage space full. Cleaning up old data...');

        try {
            cleanupExpiredStorageItems();
            cleanupOldEntries();

            const nonEssentialPrefixes = ['dashboard_cache_', 'temp_', 'cache_'];
            [localStorage, sessionStorage].forEach(storage => {
                const keysToRemove = [];
                for (let i = 0; i < storage.length; i++) {
                    const key = storage.key(i);
                    if (key && nonEssentialPrefixes.some(prefix => key.startsWith(prefix))) {
                        keysToRemove.push(key);
                    }
                }
                keysToRemove.forEach(key => {
                    try {
                        storage.removeItem(key);
                    } catch (error) {
                        console.error(`Error removing ${key}:`, error);
                    }
                });
            });

            toast.success('Storage cleanup completed.');

        } catch (cleanupError) {
            console.error('Error during storage cleanup:', cleanupError);
            toast.error('Storage cleanup failed. Please clear your browser data manually.');

            setTimeout(() => {
                if (window.confirm('Automatic cleanup failed. Would you like to clear all local data and refresh? This will sign you out.')) {
                    try {
                        localStorage.clear();
                        sessionStorage.clear();
                        window.location.reload();
                    } catch (error) {
                        console.error('Manual cleanup failed:', error);
                        toast.error('Please manually clear your browser storage for this site.');
                    }
                }
            }, 1000);
        }
    };

    if (auth.isLoading) {
        return (
            <Dimmer inverted active={true}>
                <Loader inverted > Loading...</Loader>
            </Dimmer>
        )
    }

    if (sessionExpired) {
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <h2>Session Expired</h2>
                <p>Your session has expired. Please log in again to continue.</p>
                <button
                    style={{
                        marginTop: '20px',
                        padding: '10px 24px',
                        fontSize: '1rem',
                        cursor: 'pointer'
                    }}
                    onClick={() => {
                        setSessionExpired(false);
                        auth.signinRedirect();
                    }}
                >
                    Re-authenticate
                </button>
            </div>
        );
    }

    if (auth.error &&
        auth.error.message !== 'login_required' &&
        auth.error.message !== 'interaction_required' &&
        !auth.error.message?.includes('token_expired') &&
        !auth.error.message?.includes('QuotaExceededError')) {
        return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
                <h3>Authentication Error</h3>
                <p>Please try logging in again.</p>
                <button onClick={() => auth.signinRedirect()}>Login</button>
            </div>
        );
    }

    if (auth.isAuthenticated) {
        return (
            <AuthContext.Provider value={{ auth, openIdSettings, setOpenIdSettings }}>
                <ToastContainer />
                <App />

                {/* Save Warning Modal */}
                {showSaveWarning && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        zIndex: 10000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <div style={{
                            backgroundColor: 'white',
                            padding: '30px',
                            borderRadius: '8px',
                            maxWidth: '500px',
                            textAlign: 'center',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
                        }}>
                            <h2 style={{ marginBottom: '20px', color: '#f39c12' }}>Session Expiring Soon</h2>
                            <p style={{ marginBottom: '20px', fontSize: '16px', lineHeight: '1.5' }}>
                                Your session will expire in about 5 minutes. Please save any unsaved work before continuing.
                            </p>
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                                <button
                                    style={{
                                        padding: '10px 20px',
                                        fontSize: '14px',
                                        backgroundColor: '#3498db',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                    onClick={handleExtendSession}
                                >
                                    Extend Session Now
                                </button>
                                <button
                                    style={{
                                        padding: '10px 20px',
                                        fontSize: '14px',
                                        backgroundColor: '#95a5a6',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                    onClick={handleDismissSaveWarning}
                                >
                                    I'll Save and Wait
                                </button>
                            </div>
                            <p style={{ marginTop: '15px', fontSize: '12px', color: '#7f8c8d' }}>
                                The session will automatically refresh in 2 minutes if no action is taken.
                            </p>
                        </div>
                    </div>
                )}
            </AuthContext.Provider>
        );
    } else {
        if (!sessionExpired) {
            auth.signinRedirect();
        }
        return (
            <Dimmer inverted active={true}>
                <Loader inverted>Signing In...</Loader>
            </Dimmer>
        );
    }
};

export default OpenId;